import { CommonModule } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID } from '@angular/core';
import { buildProductTitle } from '../list/list-grouping.util';

/**
 * The itemized product list, with a Total row, shown by Submit All Quotes and
 * Accept All Offers (mockup plate 07), so both confirmations name each
 * product, its due date and its fee the same way.
 */
@Component({
  selector: 'app-bulk-item-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bulk-item-list.component.html',
  styleUrl: './bulk-item-list.component.scss',
})
export class BulkItemListComponent {
  /** The products being written, as `SiteDetail` rows. */
  @Input() rows: readonly any[] = [];
  /** Prefixes the `-items`, `-item`, `-item-price` and `-total` test ids. */
  @Input() testIdPrefix = '';
  /**
   * Accessible name for the scrolling list. The list caps at four rows and
   * scrolls, and a scroll container holding no focusable element is not
   * reachable by keyboard in every browser, so it is a tab stop and needs a
   * name of its own.
   */
  @Input() listLabel = 'Products in this request';
  /**
   * Whether a row with no due date shows its turn time instead. A quote
   * carries a vendor-proposed turn time; an offer carries only a due date,
   * so Accept All Offers shows nothing in that column when the date is absent.
   */
  @Input() turnTimeFallback = true;

  constructor(@Inject(LOCALE_ID) public locale: string) {}

  /** Sum of `proposal.bid` across the rows, for the Total row. */
  get totalPrice(): number {
    return this.rows.reduce((sum, detail) => sum + (detail?.proposal?.bid ?? 0), 0);
  }

  rowTitle(detail: any): string {
    return buildProductTitle(detail?.site);
  }

  /**
   * The due date for one row, when it has one: the site's own
   * `desiredByDate` takes priority — the vendor's quote form never showed a
   * turn time input for it, see `submitAllQuotes` in
   * `bulk-eligibility.util.ts` — falling back to `proposedDeliverDate`.
   * Mirrors `product-line.component.ts`'s `offerDueDate` getter, widened
   * with the same `desiredByDate` arm `submitAllQuotes` accepts.
   */
  rowDueDate(detail: any): any {
    return detail?.site?.desiredByDate ?? detail?.proposal?.proposedDeliverDate ?? null;
  }

  /** Turn time in business days for one row. */
  rowTurnTime(detail: any): number {
    return detail?.proposal?.proposedTurnTime ?? 0;
  }
}
